/** @jsx jsx */
import React, {
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  memo,
} from 'react';
import { useField, FastField, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/core';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card as MuiCard,
  CardContent,
  Collapse,
  Dialog,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import DynamicFeedIcon from '@material-ui/icons/DynamicFeed';
import EuroIcon from '@material-ui/icons/Euro';
import CloseIcon from '@material-ui/icons/Close';

import trashIcon from 'assets/icons/trash.svg';
import ArrowCheckButton from 'components/ArrowCheckButton';
import { getResultUnit } from 'utils';
import { IChallengeListProductItem } from 'interfaces/challenge.types';
import { ICustomTheme } from 'interfaces';
import { challengeSelector } from 'reducers/challenge.reducer';
import { productCurrencySelector } from 'reducers/app.reducer';
import ProductThumbnail from '../../ProductsDetailsList/ProductsThumbnail';
import * as S from '../styles';
import { STEPS } from '../constants';

const PIXEL_BORDER = '3px';
const PAPER_DIALOG_HEIGHT = '60%';
const MAX_CHARACTERS = 70;

const useStyle = makeStyles<ICustomTheme>((theme: ICustomTheme) => ({
  tooltip: {
    maxWidth: 250,
    padding: theme.spacing(2),
    backgroundColor: theme.colors.grey.light5,
    boxShadow: theme.boxShadow,
  },
  typo: {
    margin: 0,
    color: theme.colors.grey.main,
    fontSize: theme.typography.body2.fontSize,
  },
  label: {
    wordBreak: 'break-word',
  },
}));

const TooltipLongLabel = ({
  label,
  withBottomLabel = false,
}: {
  label: string;
  withBottomLabel: boolean;
}) => {
  const classes = useStyle();
  const slicedLabel = label.slice(0, MAX_CHARACTERS - 1);
  return (
    <Tooltip
      classes={{
        tooltip: classes.tooltip,
      }}
      title={(
        <Typography className={classes.typo} paragraph>
          {label}
        </Typography>
      )}
      placement={withBottomLabel ? 'bottom-start' : 'top-start'}
    >
      <span className={classes.label}>{`${slicedLabel}...`}</span>
    </Tooltip>
  );
};

function Input({
  name,
  label,
  isAutoFocused = false,
  withBottomLabel = false,
}: {
  name: string;
  label: string;
  isAutoFocused?: boolean;
  withBottomLabel?: boolean;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();

  return (
    <FastField name={name}>
      {({ field, meta }: FieldProps) => {
        return (
          <S.CardInput
            type="number"
            InputProps={{
              disableUnderline: true,
              autoFocus: isAutoFocused,
              style: { marginTop: withBottomLabel ? 0 : theme.spacing(1) },
            }}
            InputLabelProps={{ shrink: false }}
            bottomLabel={withBottomLabel}
            label={
              label.length < MAX_CHARACTERS ? (
                label
              ) : (
                <TooltipLongLabel
                  label={label}
                  withBottomLabel={withBottomLabel}
                />
              )
            }
            error={meta.touched && !!meta.error}
            {...field}
            onWheel={(e: any) => {
              // Prevent the input value change on scroll event
              e.target.blur();
            }}
          />
        );
      }}
    </FastField>
  );
}

const Card = memo(function ({
  fieldId,
  isEditing,
  isSelected,
  product,
  isValueFieldRequired,
  error,
  errorMessage,
  openDialog,
  closeDialog,
  select,
  reset,
}: {
  fieldId: string;
  isEditing: boolean;
  isSelected: boolean;
  product: IChallengeListProductItem;
  isValueFieldRequired: boolean;
  error: boolean;
  errorMessage?: string;
  openDialog: () => void;
  closeDialog: () => void;
  select: () => void;
  reset: () => void;
}) {
  const theme: ICustomTheme = useTheme();
  const { t } = useTranslation();
  const productCurrency = useSelector(productCurrencySelector);
  const { id }: { id: string } = useParams();
  const { data: challengeData } = useSelector(challengeSelector(+id));

  const [cardHeight, setCardHeight] = useState<number>();
  const cardRef = useRef<HTMLDivElement>(null);

  const isDesktop = !useMediaQuery(theme.breakpoints.down('sm'));

  useLayoutEffect(() => {
    setCardHeight(cardRef.current?.getBoundingClientRect().height);
  }, []);

  return (
    <MuiCard
      css={{
        width: isDesktop ? 350 : '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        padding: '2px !important',
        outline: isSelected
          ? `${PIXEL_BORDER} solid ${theme.colors.paletteFromBO.secondary}`
          : 'none',
      }}
      ref={cardRef}
    >
      {/* ----- CARD HEADER -----*/}
      <Box
        css={{
          height: 50,
          backgroundColor: isSelected
            ? theme.colors.paletteFromBO.secondary
            : theme.colors.grey.light2,
        }}
      >
        <Typography
          variant="body2"
          align="right"
          css={{
            fontWeight: theme.typography.fontWeightBold,
            padding: theme.spacing(2, 2, 2, 0),
            color: theme.colors.white,
          }}
        >
          {challengeData.isRewardingGlobal ? (
            <span>
              {product.coefficient} 
              {' '}
              {getResultUnit(challengeData)}
            </span>
          ) : (
            <React.Fragment>
              {product.reward} 
              {' '}
              {productCurrency}
              {' '}
              <span>{t('challenges.declaration.thresholdUnit')}</span>
              {' '}
              {product.threshold! > 1 && (
                <span css={{ fontWeight: theme.typography.fontWeightMedium }}>
                  {t('challenges.declaration.thresholdTitle', {
                    threshold: product.threshold,
                  })}
                </span>
              )}
            </React.Fragment>
          )}
        </Typography>
      </Box>
      {/* ----- CARD PRODUCT DESCRIPTION -----*/}
      <Grid
        css={{
          padding: theme.spacing(1),
          flex: 1,
        }}
      >
        <Grid>
          <S.StyledTypography variant="body2">
            {product.challengeProduct.range}
          </S.StyledTypography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            {product.challengeProduct.unit}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            <span css={{ fontWeight: theme.typography.fontWeightBold }}>
              {product.challengeProduct.brand}
            </span>
            {product.challengeProduct.brand && <span> - </span>}
            {product.challengeProduct.productName}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            {product.challengeProduct.productCode}
          </Typography>
        </Grid>
        <Grid>
          {product.challengeProduct.productDescription && (
            <Typography variant="body2" css={S.cssProductDescription}>
              {product.challengeProduct.productDescription}
            </Typography>
          )}
        </Grid>
      </Grid>
      {/* ----- CARD PRODUCT IMG -----*/}
      <CardContent>
        <ProductThumbnail thumbnail={product.challengeProduct.visual} />
      </CardContent>
      {isSelected && !isEditing ? (
        <S.EditionPaper>
          <Grid container alignItems="center">
            <Grid item>
              <S.EditButton aria-label="edit product" onClick={openDialog}>
                <CreateOutlinedIcon fontSize="large" />
              </S.EditButton>
            </Grid>
            <Grid item css={{ flexGrow: 1 }}>
              <FastField name={`${STEPS.products}.${fieldId}.selectedUnits`}>
                {({ field }: FieldProps) => (
                  <Typography align="center">
                    {field.value} 
                    {' '}
                    <DynamicFeedIcon />
                  </Typography>
                )}
              </FastField>
            </Grid>
            {isValueFieldRequired && (
              <Grid item css={{ flexGrow: 1 }}>
                <FastField name={`${STEPS.products}.${fieldId}.selectedValue`}>
                  {({ field }: FieldProps) => (
                    <Typography align="center">
                      {field.value} 
                      {' '}
                      <EuroIcon />
                    </Typography>
                  )}
                </FastField>
              </Grid>
            )}
            <Grid item>
              <S.ResetButton aria-label="reset product" onClick={reset}>
                <img src={trashIcon} alt="trash" />
              </S.ResetButton>
            </Grid>
          </Grid>
        </S.EditionPaper>
      ) : (
        <S.AddButton aria-label="add product" onClick={openDialog}>
          <Add fontSize="large" />
        </S.AddButton>
      )}
      <Dialog
        onClose={closeDialog}
        aria-label="Product form"
        open={isEditing}
        container={() => cardRef.current}
        BackdropProps={{ style: { position: 'absolute' } }}
        style={{ position: 'absolute', height: '100%' }}
        PaperProps={{
          style: {
            height: PAPER_DIALOG_HEIGHT,
            width: '100%',
            boxSizing: 'border-box',
            position: 'initial',
            border: errorMessage ? `${PIXEL_BORDER} solid` : 'none',
            borderColor: errorMessage
              ? theme.colors.error.main
              : theme.colors.white,
            transition: 'border-color 1s ease-out',
            margin: theme.spacing(2),
          },
        }}
      >
        <div
          css={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            css={{
              display: 'flex',
              flex: 1,
              alignItems: isValueFieldRequired ? 'flex-end' : 'center',
              justifyContent: 'center',
              padding: isValueFieldRequired
                ? theme.spacing(3, 1, 1)
                : theme.spacing(3),
            }}
          >
            <Input
              name={`${STEPS.products}.${fieldId}.units`}
              label={product.challengeProduct.unit}
              isAutoFocused
            />
          </div>
          {isValueFieldRequired && (
            <div
              css={{
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                padding: theme.spacing(1, 1, 3),
                backgroundColor: theme.colors.grey.light5,
              }}
            >
              <Input
                name={`${STEPS.products}.${fieldId}.value`}
                label="€"
                withBottomLabel
              />
            </div>
          )}
        </div>
        <ArrowCheckButton
          css={() =>
            css({
              position: 'absolute',
              bottom: `calc((100% - ${PAPER_DIALOG_HEIGHT}) / 2)`,
              left: '50%',
              transform: 'translate(-50%, 50%)',
              zIndex: 1302,
            })}
          isClicked={isSelected && !isEditing}
          onClick={select}
          disabled={error || !isEditing}
          error={!!errorMessage}
        />
        <IconButton
          aria-label="close dialog"
          onClick={closeDialog}
          css={{
            position: 'absolute',
            top: theme.spacing(1),
            right: theme.spacing(1),
            color: theme.colors.white,
          }}
        >
          <CloseIcon style={{ fontSize: 26 }} />
        </IconButton>
        <Collapse
          in={!!errorMessage}
          timeout={1000}
          css={{
            width: '100%',
            position: 'absolute',
            bottom: 0,
            left: 0,
            zIndex: 1301,
            background: theme.colors.error.main,
          }}
        >
          <Box
            css={{
              color: theme.colors.white,
              height: `calc((${cardHeight}px - ((${cardHeight}px * ${PAPER_DIALOG_HEIGHT}) - ${PIXEL_BORDER})) / 2)`,
              padding: theme.spacing(3, 1, 1),
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              align="center"
              css={{
                fontWeight: theme.typography.fontWeightBold,
                fontSize: theme.typography.caption.fontSize,
              }}
            >
              {errorMessage}
            </Typography>
          </Box>
        </Collapse>
      </Dialog>
    </MuiCard>
  );
});

export default function ProductCard({
  product,
  fieldId,
  isValueFieldRequired,
}: {
  product: IChallengeListProductItem;
  fieldId: string;
  isValueFieldRequired: boolean;
}): JSX.Element {
  const [isSelectedField, , isSelectedHelpers] = useField(
    `${STEPS.products}.${fieldId}.isSelected`,
  );
  const [isEditingField, , isEditingHelpers] = useField(
    `${STEPS.products}.${fieldId}.isEditing`,
  );
  const [, unitsMeta, unitsHelpers] = useField(
    `${STEPS.products}.${fieldId}.units`,
  );
  const [, selectedUnitsMeta, selectedUnitsHelpers] = useField(
    `${STEPS.products}.${fieldId}.selectedUnits`,
  );
  const [, valueMeta, valueHelpers] = useField(
    `${STEPS.products}.${fieldId}.value`,
  );
  const [, selectedValueMeta, selectedValueHelpers] = useField(
    `${STEPS.products}.${fieldId}.selectedValue`,
  );

  const error = !!(unitsMeta.error || valueMeta.error);
  const errorMessage =
    (unitsMeta.touched && unitsMeta.error) ||
    ((valueMeta.touched && valueMeta.error) as string);

  const handleOpenDialog = useCallback((): void => {
    isEditingHelpers.setValue(true);
  }, [isEditingHelpers]);

  const handleCloseDialog = useCallback((): void => {
    unitsHelpers.setTouched(false);
    valueHelpers.setTouched(false);
    isEditingHelpers.setValue(false);
  }, [unitsHelpers, valueHelpers, isEditingHelpers]);

  const handleSelection = useCallback((): void => {
    isSelectedHelpers.setValue(true);
    selectedUnitsHelpers.setValue(unitsMeta.value);
    if (isValueFieldRequired) {
      selectedValueHelpers.setValue(valueMeta.value);
    }
    handleCloseDialog();
  }, [
    handleCloseDialog,
    unitsMeta.value,
    valueMeta.value,
    isSelectedHelpers,
    isValueFieldRequired,
    selectedUnitsHelpers,
    selectedValueHelpers,
  ]);

  const handleReset = useCallback((): void => {
    isSelectedHelpers.setValue(false);
    selectedUnitsHelpers.setValue(selectedUnitsMeta.initialValue);
    unitsHelpers.setValue(unitsMeta.initialValue);
    if (isValueFieldRequired) {
      selectedValueHelpers.setValue(selectedValueMeta.initialValue);
      valueHelpers.setValue(valueMeta.initialValue);
    }
  }, [
    isSelectedHelpers,
    isValueFieldRequired,
    selectedUnitsHelpers,
    selectedUnitsMeta,
    selectedValueHelpers,
    selectedValueMeta,
    unitsHelpers,
    unitsMeta,
    valueHelpers,
    valueMeta,
  ]);

  return (
    <Card
      fieldId={fieldId}
      isEditing={isEditingField.value}
      isSelected={isSelectedField.value}
      product={product}
      isValueFieldRequired={isValueFieldRequired}
      error={error}
      errorMessage={errorMessage}
      openDialog={handleOpenDialog}
      closeDialog={handleCloseDialog}
      select={handleSelection}
      reset={handleReset}
    />
  );
}
